import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';

import dayjs from 'dayjs';

import {
  checkIsLoading,
  checkIsLoadingDone,
  checkIsLoadingError
} from 'common/utils/checkLoading';

import {
  CarNavigationHistoryPathType,
  CarLocationSharingMovementHistoryPoint
} from '../../types';

import {
  CARS_STATE,
  CAR_NAVIGATION_HISTORY_PATH_TYPES
} from '../../constants';
import { EMPTY_ARRAY } from 'shared/constants';

const useLocationHistorySharingLoadingStatus = () => useSelector(
  (state: RootState) => state[CARS_STATE].locationHistorySharing.loadingStatus
);

export const useIsLocationHistorySharingLoading = () => {
  const locationHistorySharingLoadingStatus = useLocationHistorySharingLoadingStatus();
  return checkIsLoading(locationHistorySharingLoadingStatus);
};

export const useIsLocationHistorySharingDone = () => {
  const locationHistorySharingLoadingStatus = useLocationHistorySharingLoadingStatus();
  return checkIsLoadingDone(locationHistorySharingLoadingStatus);
};

export const useIsLocationHistorySharingError = () => {
  const locationHistorySharingLoadingStatus = useLocationHistorySharingLoadingStatus();
  return checkIsLoadingError(locationHistorySharingLoadingStatus);
};

export const useLocationHistorySharing = () => useSelector(
  (state: RootState) => state[CARS_STATE].locationHistorySharing.data
);

export const useIsInactiveLocationHistorySharing = () => {
  const { movementHistory: { startDateUtc = undefined } = {} } = useLocationHistorySharing() || {};

  return dayjs().isBefore(startDateUtc);
};

export const useIsActiveLocationHistorySharing = () => {
  const { movementHistory: { startDateUtc = undefined, endDateUtc = undefined } = {} } = useLocationHistorySharing() || {};
  return dayjs().isBetween(startDateUtc, endDateUtc);
};

export const useIsFinishedLocationHistorySharing = () => {
  const { movementHistory: { endDateUtc = undefined } = {} } = useLocationHistorySharing() || {};
  return dayjs().isAfter(endDateUtc);
};

export const useLocationHistorySharingSelectedLocation = () => useSelector(
  (state: RootState) => state[CARS_STATE].locationHistorySharing.selectedLocation
);

export const useLocationHistorySharingTypes = () => {
  const locationHistorySharing = useLocationHistorySharing();

  return useMemo(() => {
    if (locationHistorySharing) {
      const locationTypes = new Set<CarNavigationHistoryPathType>();

      locationHistorySharing.movementHistory.path.forEach(({ type }) => {
        locationTypes.add(type);
      });

      return Array.from(locationTypes);
    }

    return EMPTY_ARRAY;
  }, [locationHistorySharing]);
};

export const useLocationHistorySharingFilter = () => useSelector(
  (state: RootState) => state[CARS_STATE].locationHistorySharing.filter
);

export const useFilteredLocationHistorySharing = () => {
  const locationHistorySharing = useLocationHistorySharing();
  const { type } = useLocationHistorySharingFilter();

  return useMemo(() => {
    if (locationHistorySharing && type !== CAR_NAVIGATION_HISTORY_PATH_TYPES.all) {
      return {
        ...locationHistorySharing,
        movementHistory: {
          ...locationHistorySharing.movementHistory,
          path: locationHistorySharing.movementHistory.path.filter(item => item.type === type)
        }
      }
    }

    return locationHistorySharing;
  }, [locationHistorySharing, type]);
};

export const useHasLocationHistorySharing = () => {
  const isLocationHistorySharingDone = useIsLocationHistorySharingDone();
  const { movementHistory: { startLocation = null, endLocation = null, path = [] } = {} } = useLocationHistorySharing() || {};

  return isLocationHistorySharingDone && !!startLocation && !!endLocation && !!path && !!path.length;
};

export const useLocationHistorySharingFlatPath = () => {
  const locationHistorySharing = useLocationHistorySharing();

  return useMemo(() => {
    if (locationHistorySharing) {
      const { movementHistory: { startLocation, path, endLocation } } = locationHistorySharing;
      let result: CarLocationSharingMovementHistoryPoint[] = [];

      if (startLocation) {
        result.push({
          latitude: startLocation.latitude,
          longitude: startLocation.longitude,
          timestampUtc: startLocation.timestampUtc
        });
      }

      const waypoints = path.reduce(
        (acc, { locations }) => acc.concat(locations),
        [] as CarLocationSharingMovementHistoryPoint[]
      );

      result = result.concat(waypoints);

      if (endLocation) {
        result.push({
          latitude: endLocation.latitude,
          longitude: endLocation.longitude,
          timestampUtc: endLocation.timestampUtc
        });
      }

      return result;
    }

    return [];
  }, [locationHistorySharing]);
};

export const useMapLocationHistorySharingFlatPath = () => {
  const locationHistorySharing = useLocationHistorySharing();

  return useMemo(() => {
    if (locationHistorySharing) {
      const { movementHistory: { path } } = locationHistorySharing;
      return path.reduce(
        (acc, { locations }, index) => {
          if (index === 0) {
            return acc.concat(locations);
          }

          acc.push(locations[1]);
          return acc;
        },
        [] as CarLocationSharingMovementHistoryPoint[]
      );
    }

    return [];
  }, [locationHistorySharing]);
};

export const useFilteredLocationHistorySharingFlatPath = () => {
  const filteredLocationHistorySharing = useFilteredLocationHistorySharing();

  return useMemo(() => {
    if (filteredLocationHistorySharing) {
      const { movementHistory: { startLocation, path, endLocation } } = filteredLocationHistorySharing;
      let result: CarLocationSharingMovementHistoryPoint[] = [];

      if (startLocation) {
        result.push({
          latitude: startLocation.latitude,
          longitude: startLocation.longitude,
          timestampUtc: startLocation.timestampUtc
        });
      }

      const waypoints = path.reduce(
        (acc, { locations }) => acc.concat(locations),
        [] as CarLocationSharingMovementHistoryPoint[]
      );

      result = result.concat(waypoints);

      if (endLocation) {
        result.push({
          latitude: endLocation.latitude,
          longitude: endLocation.longitude,
          timestampUtc: endLocation.timestampUtc
        });
      }

      return result;
    }

    return [];
  }, [filteredLocationHistorySharing]);
};